import { useEffect, useState } from "react"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { http } from "../../shared/api/http"
import { useProfile } from "./hooks/useProfile"
import { ProfileSidebar } from "./ProfileSidebar"
import { ExperienceBlock } from "./ExperienceBlock"
import { EducationBlock } from "./EducationBlock"
import { CertificateBlock } from "./CertificateBlock"
import { PortfolioBlock } from "./PortfolioBlock"
import { AboutBlock } from "./AboutBlock"
import { InviteFriendBlock } from "./InviteFriendBlock"

const SECTIONS = [
  { id: "about",        label: "О себе" },
  { id: "invite",       label: "Пригласить друга" },
  { id: "experience",   label: "Опыт работы" },
  { id: "education",    label: "Образование" },
  { id: "certificates", label: "Сертификаты" },
  { id: "portfolio",    label: "Портфолио" },
]

const SectionNav = ({ active, onJump }: { active: string; onJump: (id: string) => void }) => (
  <div className="sticky top-14 z-[5] -mx-6 mb-4 px-6 py-2.5 bg-gray-50/90 backdrop-blur-sm border-b border-gray-100">
    <div className="flex items-center gap-1 overflow-x-auto">
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          onClick={() => onJump(s.id)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
            active === s.id
              ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
              : "text-gray-500 hover:text-gray-800 hover:bg-white"
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  </div>
)

export const ProfilePage = () => {
  const {
    profile,
    experiences,
    educations,
    certificates,
    portfolio,
    loading,
    updateProfile,
    addExperience,
    deleteExperience,
    addEducation,
    deleteEducation,
    addCertificate,
    deleteCertificate,
    addPortfolioItem,
  } = useProfile()

  const [active, setActive] = useState(SECTIONS[0].id)

  useEffect(() => {
    const uid = localStorage.getItem("user_id")
    if (uid) http.post(`/api/users/${uid}/profile/view`).catch(() => {})
  }, [])

  useEffect(() => {
    if (loading || !profile) return

    const els = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el)

    if (!els.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length) setActive(visible[0].target.id)
      },
      { rootMargin: "-110px 0px -70% 0px", threshold: 0 }
    )

    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [loading, profile])

  const jumpTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  if (loading || !profile) {
    return (
      <DashboardLayout title="Мой профиль">
        <div className="grid grid-cols-3 gap-6 items-start">
          {/* Sidebar skeleton */}
          <div className="space-y-4 animate-pulse">
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="h-20 bg-gray-100" />
              <div className="p-5 space-y-3">
                <div className="w-20 h-20 -mt-10 rounded-2xl bg-gray-200 ring-4 ring-white" />
                <div className="h-4 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
            <div className="bg-white rounded-xl border h-16 animate-pulse" />
          </div>
          {/* Content skeleton */}
          <div className="col-span-2 space-y-4 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border h-32" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Мой профиль">
      <SectionNav active={active} onJump={jumpTo} />

      <div className="grid grid-cols-3 gap-6 items-start">

        {/* Sidebar */}
        <ProfileSidebar
          profile={profile}
          stats={{
            experience: experiences.length,
            education: educations.length,
            certificates: certificates.length,
            portfolio: portfolio.length,
          }}
          onSave={updateProfile}
        />

        {/* Main content */}
        <div className="col-span-2 space-y-4">
          <AboutBlock text={profile.bio} onSave={updateProfile} />

          <InviteFriendBlock />

          <ExperienceBlock
            items={experiences}
            onAdd={addExperience}
            onDelete={deleteExperience}
          />

          <EducationBlock
            items={educations}
            onAdd={addEducation}
            onDelete={deleteEducation}
          />

          <CertificateBlock
            items={certificates}
            onAdd={addCertificate}
            onDelete={deleteCertificate}
          />
        </div>
      </div>

      {/* Portfolio full-width */}
      <div className="mt-6">
        <PortfolioBlock
          items={portfolio}
          onUpload={addPortfolioItem}
        />
      </div>
    </DashboardLayout>
  )
}
